import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Slider,
  Container,
  Paper,
  Grid,
  Autocomplete,
  Divider,
  IconButton,
  Tooltip
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getActivosPorEmpresa } from "../services/activos/activosData";
import { getVulnerabilidadPorEmpresa } from "../services/vulnerabilidades/vulnerabilidadesData";
import {
  getAmenazasVulnerabilidades,
  getFechaAmenazaVulnerabilidad,
} from "../services/amenazas/amenazasVulnerabilidades/amenazasVulnerabilidadesData";
import { createVulnerabilidadActivo } from "../services/vulnerabilidades/vulnerabilidadActivo/vulnerabilidadActivo";
import { createValorImpacto } from "../services/valorImpacto/valorImpactoData";
import { getDimensionValoracion } from "../services/dimensionValoracion/dimensionValoracionData";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export const Gestionar = () => {
  const [rowData, setRowData] = useState({
    activo: null,
    vulnerabilidades: [],
  });
  const [activos, setActivos] = useState([]);
  const [vulnerabilidades, setVulnerabilidades] = useState([]);
  const [amenazasVulnerabilidades, setAmenazasVulnerabilidades] = useState([]);
  const [dimensionesCatalogo, setDimensionesCatalogo] = useState([]);
  const [showFechaBox, setShowFechaBox] = useState({});
  const { user } = useAuth();
  const emp_codigo = user?.emp_codigo;
  const navigate = useNavigate();

  const marcasDeslizador = Array.from({ length: 10 }, (_, i) => ({
    value: i + 1,
    label: String(i + 1),
  }));

  useEffect(() => {
    const fetchDimensiones = async () => {
      try {
        const dimensionesData = await getDimensionValoracion();
        setDimensionesCatalogo(
          Array.isArray(dimensionesData)
            ? dimensionesData
            : dimensionesData?.data || []
        );
      } catch (error) {
        console.error("Error al cargar dimensiones de valoración:", error);
        const mensaje =
          error?.response?.data?.message ||
          error?.message ||
          "No se pudieron cargar las dimensiones de valoración.";
        toast.error(mensaje);
      }
    };
    fetchDimensiones();
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const activosData = await getActivosPorEmpresa(emp_codigo);
        setActivos(
          Array.isArray(activosData)
            ? activosData.map((item) => ({
              value: item.act_codigo,
              label: item.act_nombre,
            }))
            : []
        );

        const vulnerabilidadesData = await getVulnerabilidadPorEmpresa(emp_codigo);
        setVulnerabilidades(
          Array.isArray(vulnerabilidadesData)
            ? vulnerabilidadesData.map((item) => ({
              value: item.vul_codigo,
              label: item.vul_nombre,
            }))
            : []
        );

        const amenazasVulnerabilidadesData = await getAmenazasVulnerabilidades();
        setAmenazasVulnerabilidades(
          Array.isArray(amenazasVulnerabilidadesData)
            ? amenazasVulnerabilidadesData.map((item) => ({
              value: item.vul_codigo,
              label: item.vul_nombre,
              amv_fecha_inicio: item.amv_fecha_inicio,
            }))
            : []
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Error al cargar los datos.");
      }
    }
    fetchData();
  }, [emp_codigo]);

  const dimensionMap = useMemo(() => {
    const map = {
      disponibilidad: null,
      integridad: null,
      confidencialidad: null,
    };

    dimensionesCatalogo.forEach((dimension) => {
      const nombre = dimension?.div_nombre?.toLowerCase();
      if (!nombre) return;
      if (!map.disponibilidad && nombre.includes("dispon")) {
        map.disponibilidad = dimension.div_codigo;
      } else if (!map.integridad && nombre.includes("integr")) {
        map.integridad = dimension.div_codigo;
      } else if (!map.confidencialidad && nombre.includes("confid")) {
        map.confidencialidad = dimension.div_codigo;
      }
    });

    // Si todavía hay dimensiones pendientes, tomar las primeras disponibles
    const existentes = Array.isArray(dimensionesCatalogo)
      ? dimensionesCatalogo.map((dimension) => dimension.div_codigo)
      : [];

    const usados = Object.values(map).filter((value) => value !== null);
    const disponibles = existentes.filter((codigo) => !usados.includes(codigo));

    if (disponibles.length > 0) {
      if (!map.disponibilidad && disponibles[0] !== undefined) {
        map.disponibilidad = disponibles.shift();
      }
      if (!map.integridad && disponibles[0] !== undefined) {
        map.integridad = disponibles.shift();
      }
      if (!map.confidencialidad && disponibles[0] !== undefined) {
        map.confidencialidad = disponibles.shift();
      }
    }

    return map;
  }, [dimensionesCatalogo]);

  const formatearFecha = (fecha) => {
    if (!fecha) return "";
    const date = new Date(fecha);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const opcionesDeFormato = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    };
    return date.toLocaleDateString(undefined, opcionesDeFormato);
  };

  const handleActivoSelect = (selected) => {
    setRowData((prev) => ({
      ...prev,
      activo: selected,
      vulnerabilidades: [],
    }));
  };

  const handleAddVulnerabilidad = () => {
    if (!rowData.activo) {
      toast.warn(
        "Debe seleccionar un activo antes de agregar una vulnerabilidad."
      );
      return;
    }
    setRowData((prev) => ({
      ...prev,
      vulnerabilidades: [
        ...prev.vulnerabilidades,
        {
          vulnerabilidad: null,
          fechaVulnerabilidad: null,
          dimension1: 1,
          dimension2: 1,
          dimension3: 1,
          costo: "",
        },
      ],
    }));
    setShowFechaBox((prev) => ({
      ...prev,
      [rowData.vulnerabilidades.length]: false,
    }));
  };

  const handleVulnerabilidadSelect = async (vulIndex, selected) => {
    try {
      if (!selected) {
        // Handle clearing the selection
        setRowData((prev) => {
          const newVulnerabilidades = [...prev.vulnerabilidades];
          newVulnerabilidades[vulIndex] = {
            ...newVulnerabilidades[vulIndex],
            vulnerabilidad: null,
            fechaVulnerabilidad: "",
          };
          return { ...prev, vulnerabilidades: newVulnerabilidades };
        });
        setShowFechaBox((prev) => ({
          ...prev,
          [vulIndex]: false,
        }));
        return;
      }

      const fechaVulnerabilidad = await getFechaAmenazaVulnerabilidad(
        selected.value
      );
      const rawFecha =
        Array.isArray(fechaVulnerabilidad) && fechaVulnerabilidad[0]
          ? fechaVulnerabilidad[0].amv_fecha_inicio || fechaVulnerabilidad[0].amv_fecha_fin
          : null;

      const formattedFecha = rawFecha
        ? formatearFecha(rawFecha)
        : "";

      setRowData((prev) => {
        const newVulnerabilidades = [...prev.vulnerabilidades];
        newVulnerabilidades[vulIndex] = {
          ...newVulnerabilidades[vulIndex],
          vulnerabilidad: selected,
          fechaVulnerabilidad: formattedFecha,
        };
        return { ...prev, vulnerabilidades: newVulnerabilidades };
      });

      setShowFechaBox((prev) => ({
        ...prev,
        [vulIndex]: !!formattedFecha,
      }));
    } catch (error) {
      console.error("Error obteniendo la fecha de la vulnerabilidad:", error);
      toast.warn(
        "Primero debe asociar amenazas a esta vulnerabilidad para continuar."
      );
    }
  };

  const handleInputChange = (vulIndex, field, value) => {
    setRowData((prev) => {
      const newVulnerabilidades = [...prev.vulnerabilidades];
      newVulnerabilidades[vulIndex][field] = value;
      return { ...prev, vulnerabilidades: newVulnerabilidades };
    });
  };

  const handleEliminarVulnerabilidad = (vulIndex) => {
    if (rowData.vulnerabilidades.length <= 1) {
      toast.warn(
        "No puede eliminar la única vulnerabilidad. Debe haber al menos una vulnerabilidad."
      );
      return;
    }
    setRowData((prev) => {
      const newVulnerabilidades = [...prev.vulnerabilidades];
      newVulnerabilidades.splice(vulIndex, 1);
      return { ...prev, vulnerabilidades: newVulnerabilidades };
    });
    setShowFechaBox((prev) => {
      const newShowFechaBox = { ...prev };
      delete newShowFechaBox[vulIndex];
      return newShowFechaBox;
    });
  };

  const handleGuardarClick = async () => {
    if (!rowData.activo) {
      toast.error("Debe seleccionar un activo antes de guardar.");
      return;
    }
    for (const vul of rowData.vulnerabilidades) {
      if (
        !vul.vulnerabilidad ||
        !vul.dimension1 ||
        !vul.dimension2 ||
        !vul.dimension3 ||
        !vul.costo
      ) {
        toast.error("Debe completar todos los campos antes de guardar.");
        return;
      }
    }

    try {
      await saveDataToApi();
      await saveData2();
      toast.success("Información guardada con éxito.");
      setRowData({ activo: null, vulnerabilidades: [] });
      setShowFechaBox({});
    } catch (error) {
      console.error("Error al guardar el proceso:", error);
      const backendMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Ocurrió un error desconocido";

      if (
        error?.code === "ER_DUP_ENTRY" ||
        error?.errno === 1062 ||
        error?.response?.data?.code === "ER_DUP_ENTRY"
      ) {
        toast.error(
          "Ya existe una asociación previa para este activo y vulnerabilidad. Los registros se actualizaron con los nuevos valores."
        );
      } else if (backendMessage) {
        toast.error(backendMessage);
      } else {
        toast.error("Error al guardar los datos.");
      }
    }
  };

  const formatearFechaLocal = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  };

  const saveDataToApi = async () => {
    const dataToSend = rowData.vulnerabilidades
      .filter((vul) => vul.vulnerabilidad && rowData.activo && vul.costo !== "")
      .map((vul) => ({
        vul_codigo: Number(vul.vulnerabilidad.value),
        act_codigo: Number(rowData.activo.value),
        vac_costo: vul.costo === "" ? null : Number(vul.costo),
        vac_fecha_inicio: formatearFechaLocal(),
        vac_fecha_inactividad: null,
      }));

    if (dataToSend.length === 0) {
      throw new Error("No valid data to send.");
    }

    for (const data of dataToSend) {
      await createVulnerabilidadActivo(data);
    }
  };

  const saveData2 = async () => {
    const dataToSend = [];
    const { disponibilidad, integridad, confidencialidad } = dimensionMap;

    if (
      disponibilidad === null ||
      integridad === null ||
      confidencialidad === null
    ) {
      const faltantes = [
        !disponibilidad && "disponibilidad",
        !integridad && "integridad",
        !confidencialidad && "confidencialidad",
      ]
        .filter(Boolean)
        .join(", ");

      throw new Error(
        `Dimensiones de valoración no configuradas correctamente. Faltan: ${faltantes ||
        "dimensiones"}`
      );
    }

    for (const vul of rowData.vulnerabilidades) {
      if (
        !vul.vulnerabilidad ||
        !rowData.activo ||
        !vul.dimension1 ||
        !vul.dimension2 ||
        !vul.dimension3
      ) {
        continue;
      }
      const dimensions = [
        { divCodigo: disponibilidad, value: vul.dimension1 },
        { divCodigo: integridad, value: vul.dimension2 },
        { divCodigo: confidencialidad, value: vul.dimension3 },
      ];
      for (const dimension of dimensions) {
        if (dimension.value) {
          const data = {
            div_codigo: dimension.divCodigo,
            vul_codigo: Number(vul.vulnerabilidad.value),
            act_codigo: Number(rowData.activo.value),
            vai_fecha: formatearFechaLocal(),
            vai_valor: Number(dimension.value),
          };
          dataToSend.push(data);
        }
      }
    }
    if (dataToSend.length === 0) {
      throw new Error("No valid data to send.");
    }
    for (const data of dataToSend) {
      await createValorImpacto(data);
    }
  };

  const handleCancelarProceso = () => {
    setRowData({ activo: null, vulnerabilidades: [] });
    setShowFechaBox({});
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3, backgroundColor: '#F4F6F8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#001F3F' }}>
            Nuevo Proceso
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ color: '#3A6B7E' }}
          >
            Volver
          </Button>
        </Box>
        <Typography variant="subtitle1" sx={{ color: '#555', mb: 4 }}>
          Seleccione un activo y asocie las vulnerabilidades correspondientes para iniciar el proceso de evaluación.
        </Typography>

        <Grid container spacing={4}>
          {/* Selección de Activo */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#001F3F' }}>
                1. Seleccionar Activo
              </Typography>
              <Autocomplete
                options={activos}
                getOptionLabel={(option) => option.label}
                value={rowData.activo}
                onChange={(event, newValue) => handleActivoSelect(newValue)}
                disabled={rowData.vulnerabilidades.length > 0}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Activo"
                    variant="outlined"
                    fullWidth
                    helperText={rowData.vulnerabilidades.length > 0 ? "No se puede cambiar el activo con vulnerabilidades agregadas" : "Seleccione el activo a evaluar"}
                  />
                )}
              />
            </Paper>
          </Grid>

          {/* Gestión de Vulnerabilidades */}
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#001F3F' }}>
                  2. Asociar Vulnerabilidades
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddVulnerabilidad}
                  disabled={!rowData.activo}
                  sx={{
                    backgroundColor: '#3A6B7E',
                    '&:hover': { backgroundColor: '#2C5261' },
                    textTransform: 'none'
                  }}
                >
                  Añadir Vulnerabilidad
                </Button>
              </Box>

              {rowData.vulnerabilidades.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center', border: '2px dashed #ccc', borderRadius: 2, color: '#777' }}>
                  <Typography>No hay vulnerabilidades agregadas. Seleccione un activo y haga clic en "Añadir Vulnerabilidad".</Typography>
                </Box>
              ) : (
                rowData.vulnerabilidades.map((vul, index) => (
                  <Paper key={index} elevation={1} sx={{ p: 3, mb: 3, borderLeft: '6px solid #3A6B7E', borderRadius: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={11}>
                        <Autocomplete
                          options={vulnerabilidades}
                          getOptionLabel={(option) => option.label}
                          value={vul.vulnerabilidad}
                          onChange={(event, newValue) => handleVulnerabilidadSelect(index, newValue)}
                          disabled={!rowData.activo}
                          renderInput={(params) => <TextField {...params} label="Seleccione Vulnerabilidad" variant="outlined" size="small" />}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <Tooltip title="Eliminar Vulnerabilidad">
                          <IconButton
                            onClick={() => handleEliminarVulnerabilidad(index)}
                            color="error"
                            disabled={rowData.vulnerabilidades.length <= 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>

                    {showFechaBox[index] && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666', fontStyle: 'italic' }}>
                        Fecha de creación: {formatearFecha(vul.fechaVulnerabilidad || "")}
                      </Typography>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#001F3F' }}>
                      Dimensiones de Seguridad (1-10)
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" gutterBottom>Disponibilidad: {vul.dimension1}</Typography>
                        <Slider
                          value={vul.dimension1}
                          onChange={(e, value) => handleInputChange(index, "dimension1", value)}
                          step={1}
                          min={1}
                          max={10}
                          marks
                          valueLabelDisplay="auto"
                          sx={{ color: '#3A6B7E' }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" gutterBottom>Integridad: {vul.dimension2}</Typography>
                        <Slider
                          value={vul.dimension2}
                          onChange={(e, value) => handleInputChange(index, "dimension2", value)}
                          step={1}
                          min={1}
                          max={10}
                          marks
                          valueLabelDisplay="auto"
                          sx={{ color: '#3A6B7E' }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" gutterBottom>Confidencialidad: {vul.dimension3}</Typography>
                        <Slider
                          value={vul.dimension3}
                          onChange={(e, value) => handleInputChange(index, "dimension3", value)}
                          step={1}
                          min={1}
                          max={10}
                          marks
                          valueLabelDisplay="auto"
                          sx={{ color: '#3A6B7E' }}
                        />
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 2 }}>
                      <TextField
                        label="Costo del Impacto (USD)"
                        value={vul.costo}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^0-9.]/g, "");
                          if ((value.match(/\./g) || []).length > 1) {
                            value = value.substring(0, value.length - 1);
                          }
                          if (value.length > 1 && value[0] === "0" && value[1] !== ".") {
                            value = value.replace(/^0+/, "");
                            if (value === "") value = "0";
                          }
                          handleInputChange(index, "costo", value);
                        }}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1, color: '#666' }}>$</Typography>
                        }}
                      />
                    </Box>
                  </Paper>
                ))
              )}
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleCancelarProceso}
            sx={{
              borderColor: "#94a3b8",
              color: "#475569",
              "&:hover": { backgroundColor: "#f1f5f9" },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleGuardarClick}
            sx={{
              backgroundColor: '#001F3F',
              '&:hover': { backgroundColor: '#003366' },
              px: 4
            }}
          >
            Guardar Proceso
          </Button>
        </Box>
        <ToastContainer position="bottom-left" autoClose={3000} />
      </Paper>
    </Container>
  );
};
